import { useEffect } from 'react';
import { CurrentUserDto, UserLoginStatus } from '@sib/api';
import { useUserSetupStore } from '@sib/auth';
import { useOrganizationFromSearch } from '@sib/utils';
import { useFileManagerFileUploadStore } from '@spendbrain/stores/fileUploadStore';

export const useOrganizationSetup = () => {
  const { setOrganization } = useUserSetupStore();
  const organization = useOrganizationFromSearch();

  useEffect(() => {
    if (!organization) return;
    setOrganization({
      id: organization.id,
      name: organization.name,
    });
  }, [organization, setOrganization]);
};

export const useBeforeUnloadWarning = () => {
  const isUploading = useFileManagerFileUploadStore().getAreFilesUploading();

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isUploading) {
        event.preventDefault();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isUploading]);
};

interface UsePhoneNumberModalProps {
  user: CurrentUserDto | undefined;
  refetchUser: () => void;
}

export const usePhoneNumberModal = ({
  user,
  refetchUser,
}: UsePhoneNumberModalProps) => {
  const PHONE_MODAL_DISMISSED_KEY = `phone-modal-dismissed-${user?.id}`;

  const isPhoneModalDismissedInSession = () => {
    if (!user?.id) return false;

    return sessionStorage.getItem(PHONE_MODAL_DISMISSED_KEY) === 'true';
  };

  const isOpen =
    user?.userLoginStatus === UserLoginStatus.Uncompleted &&
    !isPhoneModalDismissedInSession();

  const handleSuccess = (statusUpdated: boolean) => {
    if (!user?.id) return;

    if (statusUpdated) {
      sessionStorage.removeItem(PHONE_MODAL_DISMISSED_KEY);
      refetchUser();
    } else {
      sessionStorage.setItem(PHONE_MODAL_DISMISSED_KEY, 'true');
    }
  };

  return {
    isOpen,
    handleSuccess,
  };
};
